import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ErrorMessage } from "@hookform/error-message";
import { useSelector } from "react-redux";
import useMakeRequest from "../../utils/ApiHelper";
import { ApiTypes } from "../../constants/Constants";
import { ApiConstants } from "../../constants/ApiConstants";
import contactUsValidation, { CONTACT_MESSAGE_MAX_CHAR } from "../../Validation/ContactUsValidation";
import { LanguageConstants } from "../../constants/LanguageConstants";
import { socket } from "../../utils/Socket";
import "./ContactSupportPage.scss";

interface FormInputs {
  name: string;
  email: string;
  subject: string;
  description: string;
  user_type: number;
}

const defaultFormValues: FormInputs = {
  user_type: 1,
  subject: "Contact support",
  name: "",
  email: "",
  description: "",
};

type Props = {
  idPrefix: string;
  variant: "modal" | "page";
  active?: boolean;
  showCloseButton?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  onLoadingChange?: (loading: boolean) => void;
};

function ContactSupportForm({
  idPrefix,
  variant,
  active = true,
  showCloseButton = false,
  onClose,
  onSuccess,
  onLoadingChange,
}: Props) {
  const contactAdd = useMakeRequest();
  const contactAddRef = useRef(contactAdd);
  contactAddRef.current = contactAdd;

  const profiledata = useSelector((state: any) => state?.ProfileState);
  const profile = profiledata?.data?.data;
  const [submitted, setSubmitted] = useState(false);
  const wasActiveRef = useRef(false);

  const { register, watch, setValue, reset, formState: { errors }, handleSubmit } = useForm<FormInputs>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: defaultFormValues,
    resolver: yupResolver(contactUsValidation),
  });

  const descriptionLen = (watch("description") ?? "").length;

  // Open: reset + prefill once when becoming active (modal open / page mount).
  useEffect(() => {
    if (!active) return;
    if (wasActiveRef.current) return;
    wasActiveRef.current = true;
    setSubmitted(false);
    reset(defaultFormValues, { keepErrors: false, keepDirty: false, keepTouched: false });
    if (profile) {
      setValue("name", profile.name ?? "", { shouldValidate: false, shouldDirty: false });
      setValue("email", profile.email ?? "", { shouldValidate: false, shouldDirty: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Prefill if profile loads after the form is already active.
  useEffect(() => {
    if (!active || !profile || submitted) return;
    setValue("name", profile.name ?? "", { shouldValidate: false, shouldDirty: false });
    setValue("email", profile.email ?? "", { shouldValidate: false, shouldDirty: false });
  }, [active, profile, setValue, submitted]);

  // Close: cleanup when modal hides.
  useEffect(() => {
    if (active) return;
    if (!wasActiveRef.current) return;
    wasActiveRef.current = false;
    contactAddRef.current.reset();
    setSubmitted(false);
    reset(defaultFormValues, { keepErrors: false, keepDirty: false, keepTouched: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => {
    onLoadingChange?.(Boolean(contactAdd.loading));
  }, [contactAdd.loading, onLoadingChange]);

  const onSubmit = (payload: FormInputs) => {
    contactAdd.makeRequest({
      payload,
      type: ApiTypes.POST,
      url: ApiConstants?.AddContactUs,
      toastStatus: true,
    });
  };

  useEffect(() => {
    if (!contactAdd?.status) return;
    const socketpayload = {
      sender_type: "1",
      receiver_id: "1",
    };
    socket.emit("admin_count", socketpayload, async () => {});
    contactAdd.reset();
    if (variant === "page") {
      setSubmitted(true);
      reset(defaultFormValues, { keepErrors: false, keepDirty: false, keepTouched: false });
    } else {
      onSuccess?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactAdd?.status]);

  const handleClose = () => {
    if (contactAdd.loading) return;
    onClose?.();
  };

  if (variant === "page" && submitted) {
    return (
      <div className="contact-support-page__panel contact-support-page__panel--narrow">
        <p className="contact-support-page__thanks">{LanguageConstants?.CONTACT_SUPPORT_THANKS}</p>
        <p className="contact-support-page__hint">{LanguageConstants?.CONTACT_SUPPORT_CLOSE_HINT}</p>
        <div className="contact-support-page__actions">
          <Link to="/" className="contact-support-page__link-home">
            {LanguageConstants?.CONTACT_SUPPORT_BACK_HOME}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-support-page__panel">
      <div className="contact-support-page__head">
        <h2 className="contact-support-page__title">{LanguageConstants?.FOOTER_CONTACT_SUPPORT}</h2>
        {showCloseButton ? (
          <button
            type="button"
            className="contact-support-page__close"
            onClick={handleClose}
            disabled={contactAdd.loading}
            aria-label={LanguageConstants?.CONTACT_SUPPORT_CLOSE_ARIA}
          >
            <span aria-hidden>×</span>
          </button>
        ) : null}
      </div>
      <p className="contact-support-page__lead">{LanguageConstants?.CONTACT_SUPPORT_LEAD}</p>
      <form className="contact-support-page__form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="contact-support-page__field">
          <label className="contact-support-page__label" htmlFor={`${idPrefix}-name`}>
            {LanguageConstants?.NAME}
          </label>
          <input
            id={`${idPrefix}-name`}
            type="text"
            className="contact-support-page__input"
            autoComplete="name"
            placeholder={LanguageConstants?.ENTER_FULL_NAME}
            disabled={contactAdd.loading}
            {...register("name")}
          />
          <ErrorMessage errors={errors} name="name" render={({ message }) => <p className="contact-support-page__error">{message}</p>} />
        </div>
        <div className="contact-support-page__field">
          <label className="contact-support-page__label" htmlFor={`${idPrefix}-email`}>
            {LanguageConstants?.EMAIL_ID}
          </label>
          <input
            id={`${idPrefix}-email`}
            type="email"
            className="contact-support-page__input"
            autoComplete="email"
            placeholder={LanguageConstants?.ENTER_EMAIL_ID}
            disabled={contactAdd.loading}
            {...register("email")}
          />
          <ErrorMessage errors={errors} name="email" render={({ message }) => <p className="contact-support-page__error">{message}</p>} />
        </div>
        <div className="contact-support-page__field">
          <label className="contact-support-page__label" htmlFor={`${idPrefix}-message`}>
            {LanguageConstants?.MESSAGE}
          </label>
          <div className="contact-support-page__textarea-wrap">
            <textarea
              id={`${idPrefix}-message`}
              className="contact-support-page__textarea"
              rows={8}
              maxLength={CONTACT_MESSAGE_MAX_CHAR}
              placeholder={LanguageConstants?.CONTACT_SUPPORT_MSG_PLACEHOLDER}
              disabled={contactAdd.loading}
              {...register("description")}
            />
            <span className="contact-support-page__count">
              {LanguageConstants?.CONTACT_SUPPORT_CHAR_COUNT} ({descriptionLen}/{CONTACT_MESSAGE_MAX_CHAR})
            </span>
          </div>
          <ErrorMessage errors={errors} name="description" render={({ message }) => <p className="contact-support-page__error">{message}</p>} />
        </div>
        <button type="submit" className="contact-support-page__submit" disabled={contactAdd.loading}>
          {contactAdd.loading ? "Sending…" : LanguageConstants?.SEND}
        </button>
      </form>
    </div>
  );
}

export default ContactSupportForm;
